import React, { Component } from "react";
import { Row, Col, Button, ListGroup, Form } from "react-bootstrap";
import Select from 'react-select';
import { LinkContainer } from "react-router-bootstrap";
import "containers/common/List.css";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SLMOwnerModal from "containers/slm/shared/SLMOwnerModal";
import "./SLMOwnerLinkList.css";

function getContextLabels(props) {
    const mode = props.ownerLinkMode || (props.sale_id ? "sale" : "lease");

    if (mode === "sale") {
        return {
            mode,
            groupLabel: "Buyer/Vendor",
            defaultType: { value: 2, label: "Buyer" },
            typeOptions: [{ value: 2, label: "Buyer" }, { value: 3, label: "Vendor" }],
            editPathBase: "/slmowners",
            createLabel: "Create New Owner",
        };
    }

    return {
        mode,
        groupLabel: "Lessee/Lessor",
        defaultType: { value: 0, label: "Lessee" },
        typeOptions: [{ value: 0, label: "Lessee" }, { value: 1, label: "Lessor" }],
        editPathBase: "/slmowners",
        createLabel: "Create New Owner",
    };
}

const normalizeOwnerOption = (option) => {
    if (!option) {
        return null;
    }

    const value = option.value ?? option.owner_org_id ?? option.entity_org_id ?? null;
    const label = option.label ?? option.name ?? option.owner_name ?? null;

    if (value === null || label === null) {
        return null;
    }

    return {
        ...option,
        value,
        label,
        owner_org_id: option.owner_org_id ?? value,
        name: option.name ?? label,
        phone_ext: option.phone_ext ?? "",
        phone_no: option.phone_no ?? "",
        mail_suburb: option.mail_suburb ?? "",
        mail_state: option.mail_state ?? "",
        mail_code: option.mail_code ?? "",
    };
};

const normalizeContactOption = (option) => {
    if (!option) {
        return null;
    }

    const firstName = option.first_name ?? "";
    const lastName = option.last_name ?? "";
    const fallbackLabel = `${firstName} ${lastName}`.trim();
    const value = option.value ?? option.owner_person_id ?? option.entity_person_id ?? null;
    const label = option.label ?? option.contact_name ?? fallbackLabel;

    if (value === null || !label) {
        return null;
    }

    return {
        ...option,
        value,
        label,
        owner_person_id: option.owner_person_id ?? value,
        phone_ext: option.phone_ext ?? "",
        phone_no: option.phone_no ?? "",
        email: option.email ?? "",
        first_name: option.first_name ?? firstName,
        last_name: option.last_name ?? lastName,
    };
};

export default class SLMOwnerLinkList extends Component {
    constructor(props) {
        super(props);
        this.createId = createElementIdFactory("slm-owner-link-list");
        this.tableElementIds = createReusableTableElementIds(this.createId, "main");
        this.contactOptionsByOrgId = new Map();

        this.state = {
            searching: false,
            isLoading: true,
            agencies: [],
            filter: {},
            showAgentDialog: false,
            showAlert: false,
            editedAgencies: [],
            dataSet: "ownerLinks",
            dataRecord: "ownerLink",
            dataSetPrimaryKey: "owner_link_id",
            newRecordIndex: 0,
            ownerSearchString: null,
            ownerInterstateFlag: true,
            selectedOwner: null,
            selectedContact: null,
            ownerOptions: [],
            contactOptions: [],
            isContactOptionsLoading: false,
            ownerMenuIsOpen: false,
            owner_org_id: null,
            owner_name: null,
            owner_phone: null,
            owner_phone_ext: null,
            owner_phone_no: null,
            owner_suburb: null,
            owner_state: null,
            owner_postcode: null,
            owner_person_id: null,
            contact_phone: null,
            contact_email: null,
            typeOptions: [{ value: 2, label: 'Buyer' }, { value: 3, label: 'Vendor' }],
            leaseTypeOptions: [{ value: 0, label: 'Lessee' }, { value: 1, label: 'Lessor' }],
            tableRows: this.props.data.length

        };

        this.toggleAgentModal = this.toggleAgentModal.bind(this);
        this.handleDeleteRecord = this.handleDeleteRecord.bind(this);
    }

    getOrgCacheKey = (orgId) => {
        if (orgId === undefined || orgId === null || orgId === "") {
            return null;
        }
        return String(orgId);
    }

    getCachedContactOptions = (orgId) => {
        const key = this.getOrgCacheKey(orgId);
        if (!key) {
            return null;
        }
        return this.contactOptionsByOrgId.get(key) || null;
    }

    fetchContactOptionsForOrg = async (orgId) => {
        const key = this.getOrgCacheKey(orgId);
        if (!key) {
            return [];
        }

        const fetchedOptions = await this.props.getContacts({ orgid: orgId });
        const nextOptions = (Array.isArray(fetchedOptions) ? fetchedOptions : [])
            .map((option) => normalizeContactOption(option))
            .filter(Boolean);

        this.contactOptionsByOrgId.set(key, nextOptions);
        return nextOptions;
    }

    async componentDidMount() {
        if (!this.props.isAuthenticated) {
            return;
        }

        this.setState({ isLoading: false });
    }

    async componentDidUpdate(prevProps) {}

    handleFormBlur = () => {
        // No-op: record persistence is handled via explicit change handlers.
    }

    getContextLabels = () => getContextLabels(this.props);

    async handleDataSave(data) {
        try {
            return await this.props.saveRecord(data, this.state.dataSet, this.state.dataSetPrimaryKey);
        } catch (e) {
            alert("Error saving");
        }
    }

    handleDeleteRecord = async (cellInfo, event) => {
        event.preventDefault();
        let data = [...this.props.data];
        var r = window.confirm("Are you sure you want to delete this record?");
        if (r === true) {
            const rowIndex = cellInfo?.row?.index ?? cellInfo?.index;
            if (rowIndex === undefined || rowIndex === null) {
                return;
            }

            data[rowIndex].archived_date = new Date();
            await this.handleDataSave(data[rowIndex]).then(() => {
                data.splice(rowIndex, 1);
                this.props.updateTableState(data, this.state.dataSet);
                this.props.updateVendorFields();
            }
            )
        }
    };

    handleInputChange = (cellInfo, event) => {
        let data = [...this.props.data];
        data[cellInfo.index][cellInfo.column.id] = event.target.value;
        data[cellInfo.index].modified = true;
        this.props.updateTableState(data, this.state.dataSet);
    };
    handleCheckboxChange = (cellInfo, event) => {
        let data = [...this.props.data];
        data[cellInfo.index][cellInfo.column.id] = event.target.checked;
        data[cellInfo.index].modified = true;
        this.props.updateTableState(data, this.state.dataSet);
    };

    handleDateChange = (cellInfo, event) => {
        let data = [...this.props.data];
        data[cellInfo.index][cellInfo.column.id] = new Date(event);
        data[cellInfo.index].modified = true;
        this.props.updateTableState(data, this.state.dataSet);
    }

    handleSave = async () => {
        this.setState({ showAlert: false });
        let data = [...this.props.data];
        for (var i = 0; i < data.length; i++) {
            const validation = this.props.validateRelatedRecord(data[i], this.state.dataSet);
            if (validation[0] === true) {
                if (data[i].hasOwnProperty("modified")) {
                    if (data[i].modified === true) {
                        if (!this.state.savingNewRecord) {
                            if (data[i].hasOwnProperty("new_record")) {
                                if (data[i].new_record === true) {
                                    this.setState({ savingNewRecord: true });
                                }
                            }
                            const result = await this.handleDataSave(data[i]);
                            if (result.result) {
                                data[i].modified = false;
                                if (data[i].hasOwnProperty("new_record")) {
                                    if (data[i].new_record === true) {
                                        data[i][this.state.dataSetPrimaryKey] = result.id;
                                        this.setState({ savingNewRecord: false });
                                    }
                                    data[i].new_record = false;
                                }
                            }
                            else {
                                this.setState({ errorMessage: result.error_message });
                                this.setState({ showAlert: true });
                            }
                        }
                    }
                }
            }
            else {
                this.setState({ errorMessage: validation[1] });
                this.setState({ showAlert: true });
                return;
            }
        }

        this.props.updateTableState(data, this.state.dataSet);
    }

    handleEditRecord = async (cellInfo, event) => {
        const row = cellInfo?.row?.original || cellInfo?.original;
        const rowIndex = cellInfo?.row?.index ?? cellInfo?.index;
        if (!row || rowIndex === undefined || rowIndex === null) {
            return;
        }

        const cachedContactOptions = this.getCachedContactOptions(row.owner_org_id);
        const selectedContactOption = row.owner_person_id
            ? normalizeContactOption({
                value: row.owner_person_id,
                owner_person_id: row.owner_person_id,
                label: row.contact_name || "",
                contact_name: row.contact_name,
                phone_ext: row.contact_phone_ext,
                phone_no: row.contact_phone_no,
                email: row.contact_email,
            })
            : null;
        const initialContactOptions = (cachedContactOptions && cachedContactOptions.length > 0)
            ? cachedContactOptions
            : (selectedContactOption ? [selectedContactOption] : []);

        this.setState({
            showAgentDialog: true,
            newRecordIndex: rowIndex,
            selectedContact: selectedContactOption,
            selectedOwner: normalizeOwnerOption({ value: row.owner_org_id, label: row.name, ...row }),
            selectedType: { value: row.type_cat_id, label: row.type },
            type: row.type,
            owner_name: row.name,
            owner_link_id: row.owner_link_id,
            owner_org_id: row.owner_org_id,
            owner_phone: (row.phone_ext || "") + " " + (row.phone_no || " "),
            owner_phone_ext: row.phone_ext,
            owner_phone_no: row.phone_no,
            owner_suburb: row.mail_suburb,
            owner_state: row.mail_state,
            owner_postcode: row.mail_code,
            owner_person_id: row.owner_person_id,
            contact_phone: row.contact_phone,
            contact_email: row.contact_email,
            contactOptions: initialContactOptions,
            isContactOptionsLoading: !cachedContactOptions,
        });

        if (cachedContactOptions) {
            return;
        }

        try {
            const contactOptions = await this.fetchContactOptionsForOrg(row.owner_org_id);
            this.setState({ contactOptions: contactOptions || [], isContactOptionsLoading: false });
        } catch (error) {
            console.error("Failed to load contacts", error);
            this.setState({ contactOptions: [], isContactOptionsLoading: false });
        }
    }

    addRecord = async event => {
        const { defaultType } = this.getContextLabels();
        let data = [...this.props.data];

        data.push({
            owner_org_id: null,
            owner_person_id: null,
            sale_id: this.props.sale_id ? this.props.sale_id : null,
            lease_id: this.props.lease_id ? this.props.lease_id : null,
            created_by: this.props.user,
            last_edited_by: this.props.user,
            selectedType: defaultType,
            type_cat_id: defaultType.value,
            type: defaultType.label,
            new_record: true
        });

        this.setState({
            showAgentDialog: true,
            newRecordIndex: data.length - 1,
            selectedType: defaultType,
            isContactOptionsLoading: false,
        });
        this.props.updateTableState(data, this.state.dataSet);
    }

    addAnotherRecord = async event => {
        const { defaultType } = this.getContextLabels();
        let data = [...this.props.data];

        data.push({
            owner_org_id: null,
            owner_person_id: null,
            sale_id: this.props.sale_id ? this.props.sale_id : null,
            lease_id: this.props.lease_id ? this.props.lease_id : null,
            type_cat_id: defaultType.value,
            type: defaultType.label,
            name: null,
            phone_no: "",
            phone_ext: "",
            mail_suburb: "",
            mail_state: "",
            mail_code: "",
            contact_name: "",
            contact_phone: "",
            created_by: this.props.user,
            last_edited_by: this.props.user,
            new_record: true
        });

        this.setState({
            selectedOwner: { value: null, label: null },
            selectedContact: { value: null, label: null },
            selectedType: defaultType,
            name: null,
            phone_no: "",
            phone_ext: "",
            owner_suburb: "",
            owner_state: "",
            owner_postcode: "",
            owner_phone: "",
            owner_email: "",
            contact_name: "",
            contact_phone: "",
            contact_email: "",
            isContactOptionsLoading: false,
            newRecordIndex: data.length - 1,
            data,
        });
        this.props.updateTableState(data, this.state.dataSet);
    }

    addContactRecord = async event => {
        let data = [...this.props.data];

        data.push({
            owner_org_id: this.state.owner_org_id,
            owner_person_id: null,
            sale_id: this.props.sale_id ? this.props.sale_id : null,
            lease_id: this.props.lease_id ? this.props.lease_id : null,
            type_cat_id: this.state.selectedType.value,
            type: this.state.selectedType.label,
            name: this.state.owner_name,
            phone_no: this.state.owner_phone_no,
            phone_ext: this.state.owner_phone_ext,
            mail_suburb: this.state.owner_suburb,
            mail_state: this.state.owner_state,
            mail_code: this.state.owner_postcode,
            contact_name: null,
            contact_phone: null,
            created_by: this.props.user,
            last_edited_by: this.props.user,
            new_record: true
        });

        this.setState({
            owner_person_id: null,
            contact_phone: "",
            contact_email: "",
            isContactOptionsLoading: false,
            newRecordIndex: data.length - 1,
            selectedContact: { label: null, value: null },
            data,
        });
        this.props.updateTableState(data, this.state.dataSet);
    }

    renderEditable = cellInfo => {
        const cellValue = this.props.data[cellInfo.index][cellInfo.column.id];

        switch (typeof (cellValue)) {
            case "boolean":
                return (
                    <Form.Check checked={cellValue}
                        onChange={this.handleCheckboxChange.bind(null, cellInfo)}
                        label=""
                        type="checkbox"
                        onBlur={this.handleSave.bind(null, cellInfo)}
                    />);

            case "number":
                return (
                    <input
                        onChange={this.handleInputChange.bind(null, cellInfo)}
                        onBlur={this.handleSave.bind(null, cellInfo)}
                        type="number"
                        className="no-border form-control"
                        value={cellValue}
                    />
                );

            default:
                if (cellInfo.column.id.indexOf("date") >= 0) {
                    return (
                        <DatePicker
                            selected={new Date(cellValue)}
                            onChange={this.handleDateChange.bind(null, cellInfo)}
                            onBlur={this.handleSave.bind(null, cellInfo)}
                            dateFormat="dd/MM/yyyy"
                        />);
                }
                else if (cellInfo.column.id === "name" && cellValue === "") {
                    return (
                        <Select
                            value={cellValue}
                            onChange={this.handleagencyChange}
                            options={this.props.ownerOptions}
                        />);
                }
                else {
                    return (
                        <input
                            onChange={this.handleInputChange.bind(null, cellInfo)}
                            onBlur={this.handleSave.bind(null, cellInfo)}
                            type="text"
                            className="no-border form-control"
                            value={cellValue}
                        />
                    );
                }
        }
    }

    renderTable(data) {
        const columnHelper = createColumnHelper();
        const columns = [
            columnHelper.accessor("owner_org_id", {
                id: "owner_org_id",
                header: "Agency ID",
                cell: (info) => (
                    <LinkContainer to={`../slmagency/${info.getValue()}`}>
                        <Button variant="link" size="sm" className="p-0 align-baseline">
                            {info.getValue()}
                        </Button>
                    </LinkContainer>
                ),
                size: 90,
            }),
            columnHelper.accessor("name", { id: "name", header: "Agency", size: 160 }),
            columnHelper.accessor("mail_street", { id: "mail_street", header: "Street", size: 180 }),
            columnHelper.accessor("mail_suburb", { id: "mail_suburb", header: "Suburb", size: 120 }),
            columnHelper.accessor("mail_state", { id: "mail_state", header: "State", size: 80 }),
            columnHelper.accessor("mail_code", { id: "mail_code", header: "Postcode", size: 90 }),
            columnHelper.accessor("phone_ext", { id: "phone_ext", header: "Phone Ext", size: 90 }),
            columnHelper.accessor("phone_no", { id: "phone_no", header: "Phone No", size: 120 }),
            columnHelper.accessor("email", { id: "email", header: "Email", size: 170 }),
            columnHelper.accessor("web", { id: "web", header: "Web", size: 140 }),
            columnHelper.accessor("notes", { id: "notes", header: "Notes", size: 180 }),
            columnHelper.display({
                id: "actions",
                header: "",
                size: 90,
                cell: (info) => (
                    <Button type="button" variant="danger" size="sm" onClick={(event) => this.handleDeleteRecord(info, event)}>
                        Delete
                    </Button>
                ),
            }),
        ];

        if (!data) {
            return null
        }
        else {
            const defaultPageSize = this.props.sale_id ? PAGE_SIZES.slmBuyerVendorSection : PAGE_SIZES.slmLesseLessorSection;
            return (
                <GenericTable
                    columns={columns}
                    data={data}
                    pageSize={defaultPageSize}
                    enablePagination={true}
                    paginationMode={this.props.minimalPagination ? "minimal" : "full"}
                    hasHeaderSorting={false}
                    enforceMinRowsHeight={false}
                    section="slm-owner-link-list"
                    elementIds={this.tableElementIds}
                />
            )
        }
    }

    // Prevent backdrop click from racing with a portaled Select onChange
    handleModalHide = () => {
        if (this.state.ownerMenuIsOpen) {
            this.setState({ ownerMenuIsOpen: false });
            return;
        }
        this.toggleAgentModal();
    }

    toggleAgentModal = async () => {
        this.setState(prevState => ({
            showAgentDialog: !prevState.showAgentDialog,
            ownerSearchString: "",
            ownerInterstateFlag: true,
            selectedOwner: null,
            selectedContact: null,
            selectedType: null,
            ownerMenuIsOpen: false,
            owner_org_id: null,
            owner_phone: "",
            owner_suburb: "",
            owner_state: "",
            owner_postcode: "",
            owner_person_id: null,
            contact_phone: "",
            contact_email: "",
            contactOptions: [],
            isContactOptionsLoading: false,
        }));

        let data = [...this.props.data];

        if (data[0].hasOwnProperty("type_cat_id")) {
            data.sort(function (a, b) { return a.type_cat_id - b.type_cat_id });
        }

        if (data[0].hasOwnProperty("owner_name")) {
            data.sort(function (a, b) {
                if (a.owner_name < b.owner_name) { return -1; }
                if (a.owner_name > b.owner_name) { return 1; }
                return 0;
            });
        }
        var i;
        for (i = 0; i < data.length; i++) {
            if (!data[i].owner_link_id) {
                data.splice(i, 1);
            }
        }
        this.props.updateVendorFields();
        this.props.updateTableState(data, this.state.dataSet);
        this.setState({ tableRows: this.props.data.length + 1 });
    }

    handleOwnerSearch = async () => {
        this.setState({ searching: true });
        try {
            let state = "";
            if (!this.state.ownerInterstateFlag) {
                state = this.props.state_code || "";
            }
            const ownerOptionsResponse = await this.props.getOwners({ ownername: this.state.ownerSearchString, state });
            const ownerOptions = (Array.isArray(ownerOptionsResponse) ? ownerOptionsResponse : [])
                .map((option) => normalizeOwnerOption(option))
                .filter(Boolean);
            this.setState({ searching: false, ownerMenuIsOpen: true, ownerOptions: ownerOptions });
        }
        catch {
            this.setState({ searching: false });
            alert("Error loading proposals");
        }
    }

    handleEnterOnSearch = (e) => {
        if (e.key === "Enter" || e.keyCode === 13) {
            this.handleOwnerSearch();
        }
    }

    handleFormInputChange = event => {
        this.setState({ [event.target.id]: event.target.value });
    }

    handleOwnerSearchStringChange = (event) => {
        this.setState({ ownerSearchString: event.target.value });
    }

    handleCheckBoxChange = event => {
        let name = event.target.name
        this.setState({
            [name]: event.target.checked
        })
    }

    handleOwnerChange = async (selectedOwner) => {
        let data = [...this.props.data];
        data[this.state.newRecordIndex].modified = true;

        if (selectedOwner) {
            const cachedContactOptions = this.getCachedContactOptions(selectedOwner.value);
            this.setState({
                ownerMenuIsOpen: false,
                selectedOwner: selectedOwner,
                contactOptions: cachedContactOptions || [],
                isContactOptionsLoading: !cachedContactOptions,
                modified: true,
                selectedContact: { value: null, label: null },
                owner_name: selectedOwner.name,
                owner_org_id: selectedOwner.value,
                owner_phone: (selectedOwner.phone_ext || "") + " " + (selectedOwner.phone_no || " "),
                owner_phone_ext: selectedOwner.phone_ext,
                owner_phone_no: selectedOwner.phone_no,
                owner_suburb: selectedOwner.mail_suburb,
                owner_state: selectedOwner.mail_state,
                owner_postcode: selectedOwner.mail_code,
                owner_person_id: null,
                contact_phone: "",
                contact_email: ""
            });
            data[this.state.newRecordIndex].owner_org_id = selectedOwner.value;
            data[this.state.newRecordIndex].name = selectedOwner.name;
            data[this.state.newRecordIndex].owner_name = selectedOwner.name;
            data[this.state.newRecordIndex].phone_ext = selectedOwner.phone_ext;
            data[this.state.newRecordIndex].phone_no = selectedOwner.phone_no;
            data[this.state.newRecordIndex].mail_suburb = selectedOwner.mail_suburb;
            data[this.state.newRecordIndex].mail_state = selectedOwner.mail_state;
            data[this.state.newRecordIndex].mail_code = selectedOwner.mail_code;
            data[this.state.newRecordIndex].contact_name = null;
            data[this.state.newRecordIndex].contact_phone = null;

            if (!cachedContactOptions) {
                try {
                    const contactOptions = await this.fetchContactOptionsForOrg(selectedOwner.value);
                    this.setState({ contactOptions: contactOptions || [], isContactOptionsLoading: false });
                } catch (error) {
                    console.error("Failed to load contacts", error);
                    this.setState({ contactOptions: [], isContactOptionsLoading: false });
                }
            }
        }
        else {
            this.setState({
                ownerMenuIsOpen: false,
                selectedOwner: { value: null, label: null },
                contactOptions: [],
                isContactOptionsLoading: false,
                modified: true,
                selectedContact: { value: null, label: null },
                owner_org_id: null,
                owner_name: null,
                owner_phone: "",
                owner_phone_ext: null,
                owner_phone_no: null,
                owner_suburb: "",
                owner_state: "",
                owner_postcode: "",
                owner_person_id: null,
                contact_phone: "",
                contact_email: ""
            });
                data[this.state.newRecordIndex].owner_org_id = null;
            data[this.state.newRecordIndex].name = null;
            data[this.state.newRecordIndex].phone_ext = null;
            data[this.state.newRecordIndex].phone_no = null;
            data[this.state.newRecordIndex].mail_suburb = null;
            data[this.state.newRecordIndex].mail_state = null;
            data[this.state.newRecordIndex].mail_code = null;
            data[this.state.newRecordIndex].contact_name = null;
            data[this.state.newRecordIndex].contact_phone = null;
        }
        const result = this.props.updateTableState(data, this.state.dataSet);
        if (result) {
            this.handleSave();
        }
        this.setState({ data });
    }

    handleCreateContactInputChange = async (selectedContact) => {
        if (!this.state.owner_org_id) {
            return null;
        }

        const contact = {
            entity_org_id: this.state.owner_org_id,
            owner_org_id: this.state.owner_org_id,
            owner_person_id: null,
            first_name: selectedContact,
            last_name: "",
            position: "",
            home_street: "",
            home_suburb: "",
            home_state: "",
            home_code: "",
            phone_ext: "",
            phone_no: "",
            email: "",
            notes: "",
            created_by: this.props.user,
            last_edited_by: this.props.user,
            new_record: true
        }

        const result = await this.props.saveRecord(contact, "ownerContact", "owner_person_id");
        if (result?.result) {
            const contactOptions = await this.fetchContactOptionsForOrg(this.state.owner_org_id);
            this.setState({ contactOptions: contactOptions || [] });
        }

        return result;
    }

    handleContactChange = async (selectedContact) => {
        let data = [...this.props.data];
        data[this.state.newRecordIndex].modified = true;
        if (!selectedContact) {
            this.setState({
                selectedContact: { value: null, label: null },
                modified: true,
                owner_person_id: null,
                contact_phone: "",
                contact_email: ""
            });
            data[this.state.newRecordIndex].owner_person_id = null;
            data[this.state.newRecordIndex].contact_name = null;
            data[this.state.newRecordIndex].contact_phone = null;
        }
        else {
            this.setState({
                selectedContact: selectedContact,
                modified: true,
                owner_person_id: selectedContact.value,
                contact_phone: (selectedContact.phone_ext || "") + " " + (selectedContact.phone_no || " "),
                contact_email: selectedContact.email
            });
            data[this.state.newRecordIndex].owner_person_id = selectedContact.value;
            data[this.state.newRecordIndex].contact_name = selectedContact.label;
            data[this.state.newRecordIndex].contact_phone = (selectedContact.phone_ext || "") + " " + (selectedContact.phone_no || " ");
        }
        const result = this.props.updateTableState(data, this.state.dataSet);
        if (result) {
            this.handleSave();
        }
    }

    handleTypeChange = async (selectedType) => {
        let data = [...this.props.data];
        data[this.state.newRecordIndex].modified = true;
        if (!selectedType) {
            this.setState({
                selectedType: { value: null, label: null },
                modified: true,
                type_cat_id: null,
                type: null
            });
            data[this.state.newRecordIndex].type_cat_id = null;
            data[this.state.newRecordIndex].type = null;
        }
        else {
            this.setState({
                selectedType: selectedType,
                modified: true,
                type_cat_id: selectedType.value,
                type: selectedType.label
            });
            data[this.state.newRecordIndex].type_cat_id = selectedType.value;
            data[this.state.newRecordIndex].type = selectedType.label
        }
        const result = this.props.updateTableState(data, this.state.dataSet);
        if (result) {
            this.handleSave();
        }
    }

    renderTable2(data) {
        const { groupLabel, typeOptions, createLabel } = this.getContextLabels();
        const columnHelper = createColumnHelper();
        const columns = [
            columnHelper.accessor("owner_link_id", {
                id: "owner_link_id",
                header: "Link ID",
                size: 70,
                cell: (info) => (
                    <Button variant="link" size="sm" onClick={(e) => this.handleEditRecord(info, e)}>
                        <div style={{ fontSize: "11px" }}>{info.getValue()}</div>
                    </Button>
                ),
            }),
            columnHelper.accessor("type", {
                id: "type",
                header: "Type",
                size: 90,
            }),
            columnHelper.accessor("name", {
                id: "name",
                header: "Name",
                size: 240,
                cell: (info) => (
                    <div>
                        <Button variant="link" size="sm" onClick={(e) => this.handleEditRecord(info, e)}>{info.getValue()}</Button>
                        <br />p: {info.row.original.phone_ext || ""} {info.row.original.phone_no || ""}
                        <br />e: {info.row.original.email}
                    </div>
                ),
            }),
            columnHelper.accessor("contact_name", {
                id: "contact_name",
                header: "Contact Name",
                size: 180,
                cell: (info) => (
                    <div>
                        {info.getValue()}<br />t: {info.row.original.contact_phone}
                    </div>
                ),
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                size: 90,
                cell: (info) => <Button type="button" variant="danger" size="sm" onClick={(e) => this.handleDeleteRecord(info, e)}>Remove</Button>,
            }),
        ];

        if (!data) {
            return null
        }
        else {
            const defaultPageSize = this.props.sale_id ? PAGE_SIZES.slmBuyerVendorSection : PAGE_SIZES.slmLesseLessorSection;
            return (
                <div>
                    <SLMOwnerModal
                        show={this.state.showAgentDialog}
                        onHide={this.handleModalHide}
                        groupLabel={groupLabel}
                        searchString={this.state.ownerSearchString || ""}
                        onSearchStringChange={this.handleOwnerSearchStringChange}
                        onSearchKeyDown={this.handleEnterOnSearch}
                        onSearch={this.handleOwnerSearch}
                        isSearching={Boolean(this.state.searching)}
                        interstateFlag={Boolean(this.state.ownerInterstateFlag)}
                        onInterstateFlagChange={this.handleCheckBoxChange}
                        createOwnerHref="/slmowners/new"
                        editOwnerHref={this.state.owner_org_id ? `/slmowners/${this.state.owner_org_id}` : null}
                        createButtonLabel={createLabel}
                        showAddAnotherOwnerLink={Boolean(this.state.owner_org_id)}
                        onAddAnotherOwnerLink={this.addAnotherRecord}
                        selectedOwner={this.state.selectedOwner}
                        onOwnerChange={this.handleOwnerChange}
                        ownerOptions={this.state.ownerOptions}
                        ownerMenuIsOpen={this.state.ownerMenuIsOpen}
                        ownerPhone={this.state.owner_phone || ""}
                        ownerLocale={`${this.state.owner_suburb || ""} ${(this.state.owner_state || "").trim()} ${(this.state.owner_postcode || "").trim()}`.trim()}
                        selectedContact={this.state.selectedContact}
                        onContactChange={this.handleContactChange}
                        onCreateContactOption={this.handleCreateContactInputChange}
                        contactOptions={this.state.contactOptions}
                        isContactOptionsLoading={Boolean(this.state.isContactOptionsLoading)}
                        contactPhone={this.state.contact_phone || ""}
                        contactEmail={this.state.contact_email || ""}
                        selectedType={this.state.selectedType}
                        onTypeChange={this.handleTypeChange}
                        typeOptions={typeOptions}
                        showAddAnotherContact={Boolean(this.state.owner_person_id)}
                        onAddAnotherContact={this.addContactRecord}
                    />
                    <GenericTable
                        columns={columns}
                        data={data}
                        pageSize={defaultPageSize}
                        enablePagination={data.length > defaultPageSize}
                        paginationMode={this.props.minimalPagination ? "minimal" : "full"}
                        hasHeaderSorting={true}
                        section="slm-owner-link-list"
                        elementIds={this.tableElementIds}
                    />
                </div>
            )
        }
    }

    renderList(data) {
        return [{}].concat(data).map(
            (d, i) =>
                i !== 0
                    ?
                    <ListGroup.Item key={i} as="a" header={d.name}>
                        {d.name}
                    </ListGroup.Item>
                    : ""
        );
    }

    renderComponent() {
        return (
            <div className="owners" id={this.createId("container")}>
                <Row className="show-Container g-2 mb-2">
                    <Col xs={12} className="text-right" >
                        <a target="_blank" rel="noopener noreferrer" href={`/slmowners/new`}><Button id={this.createId("action", "create-owner", "button")} variant="primary" type="button" className="slm-compact-action-btn" size="m" >Create New Owner</Button></a>{" "}
                        <Button id={this.createId("action", "add-link", "button")} variant="primary" className="slm-compact-action-btn" size="m" onClick={this.addRecord}>Add New Link</Button>
                    </Col>
                </Row>

                <Row className="show-Container g-2">
                    <Col xs={12}>
                        {!this.state.isLoading ? this.props.controlType === "table2" ? this.renderTable2(this.props.data) : this.renderTable(this.props.data) : null}
                    </Col>
                </Row>
            </div>
        );
    }

    render() {
        if (!this.props.data) {
            return null
        }
        else {
            return (
                <div className="OwnerLinkSection">
                    {this.props.isAuthenticated ? this.renderComponent() : null}
                </div>
            );
        }
    }
}
